import { Link } from 'react-router-dom'
import { FolderKanban } from 'lucide-react'
import { Kachel, KachelRaster, DashboardKopf, useDashboard } from '@/components/dashboard'
import { formCard, pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { fetchDashboard } from '../api/projects-phase-d.api'
import {
  NOTE_COLOR_CLASSES,
  TRAFFIC_LIGHT_CLASSES,
  TRAFFIC_LIGHT_REASONS,
  alsStunden,
  alsTag,
} from '../project-labels'
import type { JournalEntry } from '../types/phase-d.types'
import { geldText, type TrafficLightResult } from '@symphenia/shared-types'

/**
 * AP-35 Detailblatt 8 5.1 — **Das Modul-Dashboard.** Seit AP-40 die Startseite des
 * Hauptmenüpunkts und gebaut aus `components/dashboard/` wie alle Bereichs-Dashboards.
 *
 * Die Zahlen rechnet der Server in **einem** Aufruf. Eine Kachel, deren Baustein fehlt,
 * steht grau mit Erklärung; eine, für die das Recht fehlt, kommt gar nicht erst (AP-40 MD-7).
 *
 * Der Umschalter „alle / meine" ist eine **Wahl**, keine Rechtelage: Wer alles
 * sehen darf, will trotzdem meistens nur seine eigenen sehen.
 */
interface MeineEinteilung {
  assignmentId: string
  stepLabel: string
  phaseName: string
  projectId: string
  projectNumber: string
  positionLabel: string
  isLead: boolean
}

export default function ProjectDashboardPage() {
  const terms = useTerminology()
  const { bereich, setBereich, daten, fehler, kachel } = useDashboard('projects', (scope) => fetchDashboard({ scope }))

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={FolderKanban}
        titel={terms.management}
        untertitel="Was überfällig ist, was ansteht und worauf jemand wartet."
        bereich={bereich}
        onBereich={setBereich}
        umschalter={daten?.scopeSwitch !== false}
        beschriftung={{ all: `alle ${terms.projectPlural}`, mine: `meine ${terms.projectPlural}` }}
        stand={daten?.stand}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kachel
          titel="Überfällig"
          ton="gefahr"
          kachel={kachel('overdue')}
          hinweis={`${(kachel('overdue')?.projects as number) ?? 0} ${terms.projectPlural}, ${
            (kachel('overdue')?.positions as number) ?? 0
          } ${terms.positionPlural}`}
        />
        <Kachel titel="Montagen 14 Tage" kachel={kachel('assembly14')} />
        <Kachel titel="Meine Aufgaben" kachel={kachel('myTasks')} hinweis={`${(kachel('myTasks')?.overdue as number) ?? 0} überfällig`} />
        <Kachel
          titel="Wartet auf Freigabe"
          kachel={kachel('waitingForRelease')}
          hinweis={`${(kachel('waitingForRelease')?.execution as number) ?? 0} ${terms.positionPlural}, ${
            (kachel('waitingForRelease')?.workSteps as number) ?? 0
          } Arbeitsschritte`}
        />
        {/* Blatt 11 11.: aus der Einteilung (Phase D2). */}
        <Kachel titel="Nicht eingeteilt (7 Tage)" kachel={kachel('unassignedWeek')} hinweis="fällige Arbeitsschritte ohne Einteilung" />
      </div>

      {((kachel('myAssignmentsToday')?.items as MeineEinteilung[]) ?? []).length > 0 && (
        <div className={formCard}>
          <h2 className="mb-2 text-sm font-medium">Meine Einteilung heute</h2>
          <ul className="space-y-1 text-sm">
            {((kachel('myAssignmentsToday')?.items as MeineEinteilung[]) ?? []).map((e) => (
              <li key={e.assignmentId} className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-muted-foreground">{e.phaseName}</span>
                <Link to={`/projects/${e.projectId}`} className="min-w-0 flex-1 truncate hover:underline">
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{e.projectNumber}</span>
                  {e.positionLabel} · {e.stepLabel}
                </Link>
                {e.isLead && <span className="shrink-0 text-xs text-primary">führt</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className={formCard}>
          <h2 className="mb-2 text-sm font-medium">{terms.projectPlural} in Arbeit</h2>
          <ul className="space-y-2">
            {((kachel('inProgress')?.items as InArbeit[]) ?? []).map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <Link to={`/projects/${p.id}`} className="min-w-0 flex-1 truncate hover:underline">
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{p.projectNumber}</span>
                  {p.name}
                </Link>
                <span className="flex gap-1">
                  <Punkt wert={p.trafficLights.schedule} titel="Termin" />
                  <Punkt wert={p.trafficLights.cost} titel="Kosten" />
                  <Punkt wert={p.trafficLights.progress} titel="Leistungsstand" />
                </span>
                <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">
                  {alsTag(p.completionDueAt)}
                </span>
              </li>
            ))}
            {((kachel('inProgress')?.items as InArbeit[]) ?? []).length === 0 && (
              <li className="text-xs text-muted-foreground">Nichts in Arbeit.</li>
            )}
          </ul>
        </div>

        <div className={formCard}>
          <h2 className="mb-2 text-sm font-medium">Nächste Termine</h2>
          <ul className="space-y-1 text-sm">
            {((kachel('dates')?.items as TerminZeile[]) ?? []).map((t, i) => (
              <li key={`${t.phaseId}-${i}`} className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-xs text-muted-foreground">{alsTag(t.endsAt)}</span>
                <Link to={`/projects/${t.project.id}`} className="min-w-0 flex-1 truncate hover:underline">
                  {t.project.projectNumber} {t.project.name}
                </Link>
                <span className="shrink-0 text-xs text-muted-foreground">{t.phase.name}</span>
              </li>
            ))}
            {((kachel('dates')?.items as TerminZeile[]) ?? []).length === 0 && (
              <li className="text-xs text-muted-foreground">Keine Termine in den nächsten 14 Tagen.</li>
            )}
          </ul>
        </div>

        <div className={formCard}>
          <h2 className="mb-2 text-sm font-medium">Pinnwand</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {((kachel('pinboard')?.items as JournalEntry[]) ?? []).map((e) => (
              <li
                key={e.id}
                className={`rounded-md border p-2 text-sm ${NOTE_COLOR_CLASSES[e.color ?? 'YELLOW']} ${
                  e.reminderDue ? 'ring-2 ring-primary' : ''
                }`}
              >
                <p className="whitespace-pre-line">{e.text}</p>
                {e.project && (
                  <Link
                    to={`/projects/${e.project.id}`}
                    className="mt-1 block text-[11px] text-muted-foreground hover:underline"
                  >
                    {e.project.projectNumber} {e.project.name}
                  </Link>
                )}
              </li>
            ))}
            {((kachel('pinboard')?.items as JournalEntry[]) ?? []).length === 0 && (
              <li className="text-xs text-muted-foreground">Nichts angepinnt.</li>
            )}
          </ul>
        </div>

        <div className={formCard}>
          <h2 className="mb-2 text-sm font-medium">Ohne Bewegung</h2>
          <ul className="space-y-1 text-sm">
            {((kachel('idle')?.items as OhneBewegung[]) ?? []).map((p) => (
              <li key={p.id}>
                <Link to={`/projects/${p.id}`} className="hover:underline">
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{p.projectNumber}</span>
                  {p.name}
                </Link>
              </li>
            ))}
            {((kachel('idle')?.items as OhneBewegung[]) ?? []).length === 0 && (
              <li className="text-xs text-muted-foreground">
                Überall ist etwas passiert.
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Die Kacheln der Bausteine — grau mit Erklärung, wenn der Baustein fehlt; ohne Recht gar nicht. */}
      <KachelRaster>
        <Kachel
          titel="Abrechenbar"
          kachel={kachel('billable')}
          hinweis={`${geld(kachel('billable')?.amount as number)} · ${(kachel('billable')?.lines as number) ?? 0} Zeilen, ${
            (kachel('billable')?.regiePositions as number) ?? 0
          } Regie-${terms.positionPlural} (${alsStunden(kachel('billable')?.regieHours as number)} h)`}
        />
        <Kachel
          titel="Beschaffung"
          kachel={kachel('procurement')}
          hinweis={`${(kachel('procurement')?.open as number) ?? 0} offen · ${(kachel('procurement')?.inquired as number) ?? 0} angefragt · ${
            (kachel('procurement')?.ordered as number) ?? 0
          } bestellt · ${(kachel('procurement')?.overdue as number) ?? 0} überfällig · ${(kachel('procurement')?.certificateMissing as number) ?? 0} ohne Zeugnis`}
        />
      </KachelRaster>
    </div>
  )
}

// Wie im Arbeitsplatz: ohne Währungszeichen — die Zeilen können verschiedene Währungen tragen.
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

interface InArbeit {
  id: string
  projectNumber: string
  name: string
  completionDueAt: string | null
  trafficLights: { schedule: TrafficLightResult; cost: TrafficLightResult; progress: TrafficLightResult }
}

interface TerminZeile {
  phaseId: string
  endsAt: string | null
  phase: { name: string; color: string | null }
  project: { id: string; projectNumber: string; name: string }
}

interface OhneBewegung {
  id: string
  projectNumber: string
  name: string
}


function Punkt({ wert, titel }: { wert: TrafficLightResult; titel: string }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${TRAFFIC_LIGHT_CLASSES[wert.value]}`}
      title={`${titel}: ${TRAFFIC_LIGHT_REASONS[wert.reason] ?? wert.reason}`}
    />
  )
}
